/**
 * Tab-übergreifende Synchronisation des Anmeldezustands.
 *
 * Alle Fenster desselben Browsers teilen sich denselben Session-Cookie.
 * Meldet sich der Nutzer in einem Fenster ab (oder mit einem anderen
 * Unternehmen neu an), halten andere Fenster ihren alten Zustand nur noch
 * im Arbeitsspeicher — sie zeigen dann eine Identität an, die nicht mehr
 * zur Server-Session passt (Systemfehler-Meldung 30.08.26: APVOKAT-Anzeige
 * bei aktiver Polz-Session).
 *
 * Dieses Modul verteilt Anmelde-Ereignisse an alle offenen Tabs/Fenster:
 * primär über BroadcastChannel, als Fallback über das localStorage
 * "storage"-Event (feuert nur in fremden Tabs — Endlosschleifen im
 * Absender-Tab sind damit ausgeschlossen, BroadcastChannel stellt ebenfalls
 * nicht an den Absender zu).
 */

export type AuthSyncEvent =
  | { type: "logout" }
  | { type: "identity"; idUser: number; idOrganization: number };

const CHANNEL_NAME = "kars-auth-sync";
const STORAGE_KEY = "kars-auth-sync";

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME);
  }
  return channel;
}

function isAuthSyncEvent(value: unknown): value is AuthSyncEvent {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  if (v.type === "logout") return true;
  return (
    v.type === "identity" &&
    typeof v.idUser === "number" &&
    typeof v.idOrganization === "number"
  );
}

/** Informiert alle anderen offenen Tabs/Fenster über eine Anmeldeänderung. */
export function publishAuthChange(event: AuthSyncEvent): void {
  try {
    getChannel()?.postMessage(event);
  } catch {
    /* no-op: Synchronisation ist Best-Effort */
  }
  try {
    // Zeitstempel sorgt dafür, dass auch zwei gleiche Ereignisse
    // nacheinander jeweils ein storage-Event auslösen.
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...event, at: Date.now() }));
  } catch {
    /* no-op: z. B. Safari-Privatmodus ohne localStorage */
  }
}

/**
 * Registriert einen Handler für Anmeldeänderungen aus anderen Tabs.
 * Gibt eine Abmelde-Funktion zurück.
 */
export function subscribeAuthChanges(handler: (event: AuthSyncEvent) => void): () => void {
  const ch = getChannel();

  const onMessage = (e: MessageEvent) => {
    if (isAuthSyncEvent(e.data)) handler(e.data);
  };
  ch?.addEventListener("message", onMessage);

  const onStorage = (e: StorageEvent) => {
    // Mit verfügbarem BroadcastChannel kam das Ereignis bereits über den
    // Channel an — nicht doppelt verarbeiten.
    if (ch || e.key !== STORAGE_KEY || !e.newValue) return;
    try {
      const parsed: unknown = JSON.parse(e.newValue);
      if (isAuthSyncEvent(parsed)) handler(parsed);
    } catch {
      /* no-op: fremder/ungültiger Eintrag */
    }
  };
  window.addEventListener("storage", onStorage);

  return () => {
    ch?.removeEventListener("message", onMessage);
    window.removeEventListener("storage", onStorage);
  };
}
